import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import CTA, { CTAProps } from 'Src/components/buttons/CTA';
import { Check } from 'Src/feature/check/models';
import { useCheck } from 'Src/feature/check/useCheck';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { usePayment } from 'Src/feature/payment/usePayment';
import { usePaymentFlow } from 'Src/feature/payment/usePaymentFlow';
import { useTerminalSettings } from 'Src/feature/terminal/useTerminalSettings';
import { useToast } from 'Src/feature/toasts/useToast';
import useRoute from 'Src/routes/useRoute';

const sectionId = 'CheckPaymentPreAuthCTA';

type CheckPaymentPreAuthCTAProps = Omit<CTAProps, 'onClick'> & {
  payment: Check['payments'][number];
};
const CheckPaymentPreAuthCTA: FC<CheckPaymentPreAuthCTAProps> = ({ payment, ...props }) => {
  const { t } = useTranslation();
  const { route, crumbs, navigate } = useRoute();
  const toast = useToast({ persist: true });
  const log = useLogger().getSubLogger({ name: sectionId });
  const paymentFlow = usePaymentFlow();

  const adjust = usePayment.mutate.adjust();
  const terminalSettingsGet = useTerminalSettings.query();
  const checkId = route.params.active.checkId ? parseInt(route.params.active.checkId, 10) : undefined;
  const checkGetOne = useCheck.query.getOne(checkId);
  const check = checkGetOne.data;
  const paymentId = payment.id;
  const { digitalSignatureEnabled } = terminalSettingsGet.data ?? {};

  const onClick = async () => {
    // wipe out any other payment flow that "could" be in progress
    paymentFlow.reset();
    if (typeof checkId !== 'number') return log.debug('onClick', '!checkId');
    const total = check?.balance.unpaid;
    if (typeof total !== 'number') return log.debug('onClick', '!total');
    const tip = payment.tip ?? 0;

    adjust.mutate({
      payment: {
        checkId,
        paymentId,
        tip,
        total,
        isOpenTab: false,
      }
    }, {
      onSuccess: async (response) => {
        if (response.payment.paymentStatus === 'Declined') {
          toast.showAndAutoHide.warning({
            id: `${sectionId}.onClick.Declined`,
            title: t('payment.labels.declined'),
            message: t('payment.labels.paymentAdjustmentDeclined'),
          });
          return;
        }
        void checkGetOne.refetch();

        if (digitalSignatureEnabled) {
          navigate(crumbs.pos.customer.flipAndTip.start.interpolate({
            checkId,
            paymentId
          }));
        }
      },
      onError: (error) => {
        toast.show.serviceError(error, { id: `${sectionId}.onPay` });
      },
    });
  };

  return (
    <CTA
      data-testid={sectionId}
      {...props}
      onClick={onClick}
    />
  );
};

export { CheckPaymentPreAuthCTA };
export type { CheckPaymentPreAuthCTAProps };
